import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Input,
  Modal,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import StatKpi from "../kpi/StatKpi";
import styles from "./JsonlViewer.module.scss";
import {
  JsonlFetchPage,
  JsonlFieldSummary,
  JsonlPreviewLine,
  JsonlPreviewSummary,
  buildColumnsFromKeys,
  findFieldSummary,
  formatNumber,
  getRowRecord,
} from "./jsonlUtils";

const { Text } = Typography;
const PAGE_SIZE = 50;
const DEBOUNCE_MS = 350;

interface JsonlViewerProps {
  fetchPage: JsonlFetchPage;
}

const JsonlViewer: React.FC<JsonlViewerProps> = ({ fetchPage }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState<JsonlPreviewLine[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [summary, setSummary] = useState<JsonlPreviewSummary | null>(null);
  const [selectedLine, setSelectedLine] = useState<JsonlPreviewLine | null>(
    null
  );
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);

  const loadPage = useCallback(
    async (nextPage: number, nextSearch: string) => {
      setLoading(true);
      try {
        const response = await fetchPage({
          page: nextPage,
          size: PAGE_SIZE,
          search: nextSearch || undefined,
        });
        setLines(response.lines || []);
        setTotalElements(response.totalElements || 0);
        if (response.summary) {
          setSummary(response.summary);
        }
      } finally {
        setLoading(false);
      }
    },
    [fetchPage]
  );

  useEffect(() => {
    loadPage(page, search);
  }, [loadPage, page, search]);

  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      setPage(0);
      setSearch(searchInput.trim());
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [searchInput]);

  const columnKeys = useMemo(() => {
    if (summary?.columns?.length) {
      return summary.columns;
    }
    const keys = new Set<string>();
    lines.forEach((line) => {
      if (line.parsed) {
        Object.keys(line.parsed).forEach((key) => keys.add(key));
      } else {
        keys.add("parseError");
        keys.add("raw");
      }
    });
    return Array.from(keys);
  }, [summary, lines]);

  const handleColumnClick = useCallback((columnKey: string) => {
    setSelectedColumn(columnKey);
  }, []);

  const selectedFieldSummary = useMemo(
    () => findFieldSummary(selectedColumn || "", summary),
    [selectedColumn, summary]
  );

  const columns = useMemo(
    () =>
      buildColumnsFromKeys(columnKeys, t, handleColumnClick) as ColumnsType<
        Record<string, unknown>
      >,
    [columnKeys, t, handleColumnClick]
  );

  const tableData = useMemo(
    () => lines.map((line) => getRowRecord(line)),
    [lines]
  );

  return (
    <Spin spinning={loading}>
      {summary && (
        <div className={styles.summaryGrid}>
          <StatKpi
            title={t("JsonlViewer-TotalLines")}
            value={summary.totalLines}
            size="sm"
          />
          <StatKpi
            title={t("JsonlViewer-ValidLines")}
            value={summary.validLines}
            size="sm"
            valueColor="#52c41a"
          />
          <StatKpi
            title={t("JsonlViewer-InvalidLines")}
            value={summary.invalidLines}
            size="sm"
            valueColor="#ff4d4f"
          />
        </div>
      )}

      <div className={styles.filterRow}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t("JsonlViewer-SearchPlaceholder")}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />
      </div>

      <Alert
        type="info"
        showIcon
        className={styles.columnSummaryHint}
        message={t("JsonlViewer-ColumnSummaryHint")}
      />

      <Table
        size="small"
        rowKey={(record) => String(record.lineNumber)}
        columns={columns}
        dataSource={tableData}
        scroll={{ x: true }}
        pagination={{
          current: page + 1,
          pageSize: PAGE_SIZE,
          total: totalElements,
          showSizeChanger: false,
          showTotal: (total) => `${t("Common-Total")}: ${total}`,
          onChange: (nextPage) => setPage(nextPage - 1),
        }}
        onRow={(record) => ({
          onClick: () => {
            const line = lines.find(
              (item) => item.lineNumber === record.lineNumber
            );
            if (line) {
              setSelectedLine(line);
            }
          },
          style: { cursor: "pointer" },
        })}
      />

      <Modal
        title={`${t("JsonlViewer-LineDetail")} #${selectedLine?.lineNumber ?? ""}`}
        visible={!!selectedLine}
        onCancel={() => setSelectedLine(null)}
        footer={null}
        width={900}
      >
        {selectedLine?.parseError ? (
          <Tag color="error" className={styles.errorTag}>
            {selectedLine.parseError}
          </Tag>
        ) : null}
        <pre className={styles.detailPre}>
          {selectedLine?.parsed
            ? JSON.stringify(selectedLine.parsed, null, 2)
            : selectedLine?.raw}
        </pre>
        {selectedLine?.parseError ? (
          <Text type="secondary">{t("JsonlViewer-RawLine")}</Text>
        ) : null}
      </Modal>

      <Modal
        title={`${t("JsonlViewer-ColumnSummary")}: ${selectedColumn ?? ""}`}
        visible={!!selectedColumn}
        onCancel={() => setSelectedColumn(null)}
        footer={null}
        width={640}
      >
        {selectedFieldSummary ? (
          <ColumnSummaryContent summary={selectedFieldSummary} t={t} />
        ) : (
          <Text type="secondary">{t("JsonlViewer-ColumnSummaryUnavailable")}</Text>
        )}
      </Modal>
    </Spin>
  );
};

const ColumnSummaryContent: React.FC<{
  summary: JsonlFieldSummary;
  t: (key: string) => string;
}> = ({ summary, t }) => {
  if (summary.type === "NUMERIC") {
    return (
      <div className={styles.columnSummaryValues}>
        <div>
          {t("JsonlViewer-Count")}: {summary.count}
        </div>
        {summary.nullCount > 0 ? (
          <div>
            {t("JsonlViewer-NullCount")}: {summary.nullCount}
          </div>
        ) : null}
        <div>
          {t("JsonlViewer-Sum")}: {formatNumber(summary.sum ?? 0)}
        </div>
        <div>
          {t("JsonlViewer-Avg")}: {formatNumber(summary.avg ?? 0)}
        </div>
        <div>
          {t("JsonlViewer-Min")}: {formatNumber(summary.min ?? 0)} |{" "}
          {t("JsonlViewer-Max")}: {formatNumber(summary.max ?? 0)}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.columnSummaryValues}>
      <div>
        {t("JsonlViewer-Count")}: {summary.count}
      </div>
      {summary.nullCount > 0 ? (
        <div>
          {t("JsonlViewer-NullCount")}: {summary.nullCount}
        </div>
      ) : null}
      {summary.distinctCount !== undefined ? (
        <div>
          {t("JsonlViewer-DistinctCount")}: {summary.distinctCount}
        </div>
      ) : null}
      {summary.topValues?.length ? (
        <Table
          className={styles.topValuesTable}
          size="small"
          pagination={false}
          rowKey={(record) => record.value}
          columns={[
            {
              title: t("JsonlViewer-Value"),
              dataIndex: "value",
              key: "value",
              ellipsis: true,
            },
            {
              title: t("JsonlViewer-Count"),
              dataIndex: "count",
              key: "count",
              width: 100,
            },
          ]}
          dataSource={summary.topValues}
        />
      ) : null}
    </div>
  );
};

export default JsonlViewer;
